import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Select } from "@repo/o37-ui";
import {
  ArrowLeft,
  Building,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  AlertCircle
} from "lucide-react";
import { formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface CompanyMarginData {
  companyId: string;
  companyName: string;
  companyStatus: string;
  revenue: number;
  costs: number;
  margin: number;
  marginPercent: number;
}

export default function CompanyMargins() {
  const navigate = useNavigate();
  const [marginData, setMarginData] = useState<CompanyMarginData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [sortBy, setSortBy] = useState<'revenue' | 'costs' | 'margin' | 'marginPercent'>('margin');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, [filterYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Use the transaction-based P&L endpoint for actual revenue/expense data
      const startDate = `${filterYear}-01-01`;
      const endDate = `${filterYear}-12-31`;

      const response = await fetch(
        `${API_URL}/holdings/analytics/company-pl?startDate=${startDate}&endDate=${endDate}`,
        { headers: { 'x-user-id': userId, 'x-user-role': userRole } }
      );

      if (response.ok) {
        const data = await response.json();
        // Map from company-pl format (income/expenses from transactions)
        setMarginData((data.companies || []).map((c: any) => ({
          companyId: c.id,
          companyName: c.name,
          companyStatus: 'active',
          revenue: c.income || 0,
          costs: c.expenses || 0,
          margin: c.net || 0,
          marginPercent: c.income > 0 ? ((c.net / c.income) * 100) : 0
        })));
      } else {
        console.error('Failed to load P&L data:', await response.text());
        setMarginData([]);
      }
    } catch (e) {
      console.error('Failed to load margin data:', e);
      setMarginData([]);
    } finally {
      setLoading(false);
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    return [...marginData].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [marginData, sortBy, sortDir]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalRevenue = marginData.reduce((sum, d) => sum + d.revenue, 0);
    const totalCosts = marginData.reduce((sum, d) => sum + d.costs, 0);
    const totalMargin = totalRevenue - totalCosts;
    const avgMarginPercent = totalRevenue > 0 ? (totalMargin / totalRevenue * 100) : 0;
    return { totalRevenue, totalCosts, totalMargin, avgMarginPercent };
  }, [marginData]);

  const profitableCount = marginData.filter(d => d.margin > 0).length;
  const unprofitableCount = marginData.filter(d => d.margin < 0).length;

  // Get available years
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map(y => y.toString());
  }, []);

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const getMarginColor = (margin: number) => {
    if (margin > 0) return 'text-emerald-600';
    if (margin < 0) return 'text-red-600';
    return 'text-neutral-600';
  };

  const getMarginBgColor = (marginPercent: number) => {
    if (marginPercent >= 20) return 'bg-emerald-500';
    if (marginPercent >= 10) return 'bg-emerald-400';
    if (marginPercent >= 0) return 'bg-emerald-300';
    if (marginPercent >= -10) return 'bg-red-300';
    if (marginPercent >= -20) return 'bg-red-400';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Company Margins</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/revenue')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Revenue</span>
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Company Margins</h1>
            <p className="text-neutral-500">Revenue vs costs analysis by company</p>
          </div>
          <div className="w-32">
            <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-500">Total Revenue</p>
                <p className="text-xl font-semibold text-emerald-600">{formatCents(totals.totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-500">Total Costs</p>
                <p className="text-xl font-semibold text-red-600">{formatCents(totals.totalCosts)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                totals.totalMargin >= 0 ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {totals.totalMargin >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-neutral-500">Net Margin</p>
                <p className={`text-xl font-semibold ${getMarginColor(totals.totalMargin)}`}>
                  {formatCents(totals.totalMargin)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-neutral-500">Avg Margin %</p>
                <p className={`text-xl font-semibold ${getMarginColor(totals.avgMarginPercent)}`}>
                  {totals.avgMarginPercent.toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Profitable Companies</p>
                <p className="text-2xl font-semibold text-emerald-800">{profitableCount}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Unprofitable Companies</p>
                <p className="text-2xl font-semibold text-red-800">{unprofitableCount}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Breakdown */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Company Breakdown</h3>

          {sortedData.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p>No data available for {filterYear}</p>
              <p className="text-sm mt-2">Add revenue records and assign costs to companies to see margin analysis.</p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-neutral-500 px-4 py-2 border-b border-neutral-100">
                <div className="col-span-4">Company</div>
                <button
                  className={`col-span-2 text-left flex items-center gap-1 hover:text-neutral-700 ${sortBy === 'revenue' ? 'text-neutral-900' : ''}`}
                  onClick={() => handleSort('revenue')}
                >
                  Revenue {sortBy === 'revenue' && (sortDir === 'desc' ? '↓' : '↑')}
                </button>
                <button
                  className={`col-span-2 text-left flex items-center gap-1 hover:text-neutral-700 ${sortBy === 'costs' ? 'text-neutral-900' : ''}`}
                  onClick={() => handleSort('costs')}
                >
                  Costs {sortBy === 'costs' && (sortDir === 'desc' ? '↓' : '↑')}
                </button>
                <button
                  className={`col-span-2 text-left flex items-center gap-1 hover:text-neutral-700 ${sortBy === 'margin' ? 'text-neutral-900' : ''}`}
                  onClick={() => handleSort('margin')}
                >
                  Margin {sortBy === 'margin' && (sortDir === 'desc' ? '↓' : '↑')}
                </button>
                <button
                  className={`col-span-2 text-left flex items-center gap-1 hover:text-neutral-700 ${sortBy === 'marginPercent' ? 'text-neutral-900' : ''}`}
                  onClick={() => handleSort('marginPercent')}
                >
                  Margin % {sortBy === 'marginPercent' && (sortDir === 'desc' ? '↓' : '↑')}
                </button>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-neutral-100">
                {sortedData.map((company) => (
                  <div
                    key={company.companyId}
                    className="grid grid-cols-12 gap-4 items-center px-4 py-3 hover:bg-neutral-50"
                  >
                    <div className="col-span-4 flex items-center gap-3">
                      <div className="w-8 h-8 bg-neutral-100 rounded-lg flex items-center justify-center">
                        <Building className="w-4 h-4 text-neutral-500" />
                      </div>
                      <span className="font-medium text-neutral-900">{company.companyName}</span>
                    </div>
                    <div className="col-span-2 font-medium text-emerald-600">
                      {formatCents(company.revenue)}
                    </div>
                    <div className="col-span-2 font-medium text-red-600">
                      {formatCents(company.costs)}
                    </div>
                    <div className={`col-span-2 font-semibold ${getMarginColor(company.margin)}`}>
                      {formatCents(company.margin)}
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${getMarginBgColor(company.marginPercent)}`}
                          style={{ width: `${Math.min(100, Math.max(0, company.marginPercent + 50))}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${getMarginColor(company.marginPercent)}`}>
                        {company.marginPercent.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3">Understanding Margins</h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              <strong>Revenue:</strong> Income from bank transactions assigned to each company (positive amounts).
            </p>
            <p>
              <strong>Costs:</strong> Expenses from bank transactions assigned to each company (negative amounts).
            </p>
            <p>
              <strong>Margin:</strong> Revenue minus costs. Positive means profitable, negative means operating at a loss.
            </p>
            <p>
              <strong>Margin %:</strong> Margin as a percentage of revenue. Higher is better.
            </p>
          </div>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <strong>Note:</strong> This data comes from actual bank transactions. Make sure to assign companies to your transactions in the <a href="/holdings/transactions" className="underline">Transactions</a> page for accurate P&L reporting.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
